/*
This is the "Settings" page if used for the logged in user, or the
edit-user page when used to admin another user.
*/

import {uiMsg} from '../utils/Languages';
import React, {Component} from 'react';
import { Link } from 'react-router';
import {observer} from 'mobx-react';

import Paper from 'material-ui/Paper';
import CircularProgress from 'material-ui/CircularProgress';
import TextField from 'material-ui/TextField';
import {List, ListItem} from 'material-ui/List';
import Subheader from 'material-ui/Subheader';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import {Toolbar, ToolbarGroup, ToolbarSeparator, ToolbarTitle} from 'material-ui/Toolbar';
import RefreshIndicator from 'material-ui/RefreshIndicator';

import Dialog from 'material-ui/Dialog';
import Checkbox from 'material-ui/Checkbox';

import AppModel from '../DataModel/AppModel';
import AppModelUtils from '../DataModel/AppModelUtils';
import PageLoading from '../components/PageLoading';
import SelectHomeLocation from '../components/SelectHomeLocation'
import MachineList from '../components/MachineList'
import RoleChecker from '../components/RoleChecker';

import SelectLanguageCode from '../components/SelectLanguageCode';

const formStyle = {
  marginLeft: 16
};

const refresh = {
    display: 'inline-block',
    position: 'relative',
};

export default
@observer class EditUserPage extends Component {

  static contextTypes = {
    router: React.PropTypes.object
  }

  // Helper method that writes values back to datamodel as fields are changed
  handleValue(event) {
    console.log("handleValue called");
    var field = event.target.dataset.propname;
    var value = event.target.value;
    AppModel.updateUserForm.user[field]=value;
    // If the user deletes the phone number, we uncheck the SMS Notifications:
    if(field=='phone' && (!value)) {
      AppModel.updateUserForm.user.receiveSMSNotifications=false;
    }
    // If we just changed the email, we need to see if the new email is available.
    if(AppModel.updateUserForm.emailChanged
      && field=='email'
      && AppModel.updateUserForm.user.validEmail ) {
      AppModelUtils.checkIfEmailIsUnique(AppModel.updateUserForm);
    }
    if(AppModel.updateUserForm.idChanged
      && field=='id' && value>"") {
      AppModelUtils.checkUsernameAvailability(AppModel.updateUserForm);
    }


  }

  handleCheckboxToggle(field) {
    return function() {
      AppModel.updateUserForm.user[field]=!AppModel.updateUserForm.user[field];
    }
  }

  toggleDeleted(event) {
    console.log("Toggeling deleted flag");
    AppModel.updateUserForm.user.deleted=!AppModel.updateUserForm.user.deleted;
  }

  updateOrDeleteUser() {
    var isSettingsPage = AppModel.id_user == AppModel.updateUserForm.user.id;
    AppModelUtils.updateOrDeleteUser()
    .then(()=> {
      // If the phone number has been updated in the last update,
      // and the pages is used as settings-page we
      // will take the user directly to the phone verification page :
      if(AppModel.updateUserForm.phoneChanged
        && isSettingsPage
        && AppModel.updateUserForm.user.phone ) {
        // Pushing the route below will trigger reloading data for the user and
        // resetting the updateUserForm, thus we have to request the sending of
        // the verification code before pushing the route, as it is sent to the
        // current user in the updateUserForm:
        var sendPromise = AppModelUtils.sendPhoneNumberVerification();
        // Having requested a code to be sent to the user, we send her to
        // the phone-number verification page:
        this.context.router.push(
          `/confirm_phone/${AppModel.updateUserForm.user.id_customer}/${AppModel.updateUserForm.user.id}`
        )
        return sendPromise;
      }
      // If this page is being used to admin other users, than we go back
      // presumably to the user list after successful update.
      // If the page is being used as the settings page for the currently
      // logged in user, we just stay on the page  :
      if(!isSettingsPage) {
        AppModel.updateUserForm.reset();
        this.context.router.goBack();
      }
    })
    .catch(()=> {
        console.log("Update was rejected, staying on page");
    });
  }

  gotoVerifyPhonePage() {
    var id_user = AppModel.updateUserForm.user.id;
    var id_customer = AppModel.updateUserForm.user.id_customer;
    this.context.router.push(`/confirm_phone/${id_customer}/${id_user}`);
  }

  sendTestSMS() {
    AppModelUtils.sendTestSMS(AppModel.updateUserForm.user);
  }

  cancelUpdate() {
    AppModel.updateUserForm.reset();
    this.context.router.goBack();
  }

  resetUpdateOperation() {
    // When server op failed and dialog box with error has been shown.
    AppModel.updateUserForm.serverOperation.reset();
  }

  cannotEditUsers() {
    const no_action = [
      <FlatButton
        label="OK"
        primary={true}
        onTouchTap={this.cancelUpdate.bind(this)} />,
    ];
    var message =uiMsg('SP_NO_MODIFY_PERM',"You do not have permission to modify user accounts. Talk to your local area or system administrator to have permissions changed.");
    return <Dialog
      title={uiMsg('SP_INSUFFICIENT_PERM',"Insufficient permissions")}
      actions={no_action}
      modal={true}
      open={true}>
      {message}
    </Dialog>
  }

  render() {
    var form = AppModel.updateUserForm;
    var user = form.user;
    // If the user is editing himself, this page is the user's settingsPage
    // and some part of the page behaves differently in that case.
    var isSettingsPage = AppModel.id_user == form.prev_id;
    var operation = form.serverOperation;
    // Only show this page for people who can admin others, or for users that
    // try to edit their own settings page :
    // This choice can only be made after we are done fetching the
    // user and role info :
    if(  !AppModel.fetchingEditUserDetails
      && !AppModel.fetchingGrantableRoles
      && !AppModel.canAdminUsers
      && !isSettingsPage)
      return this.cannotEditUsers();
    var showFailDialog = ("FAIL"==operation.result);
    // Action to dismiss server-error dialog if it pops up :
    const actions = [
      <FlatButton
        label="OK"
        primary={true}
        onTouchTap={this.resetUpdateOperation} />,
    ];
    return (
      <PageLoading loading={operation.waiting ||
                            AppModel.fetchingEditUserDetails ||
                            AppModel.fetchingGrantableRoles }>
        <Dialog
          title={uiMsg('SP_SERVER_OP_FAILED',"Server operation failed")}
          actions={actions}
          modal={true}
          open={showFailDialog}>
          {operation.message}
        </Dialog>
        <List>
          <Subheader>{uiMsg('SP_UPDATE_USER_INFO',"Update user information")}</Subheader>
        <div style={formStyle}>
          <TextField data-propname="id"
                    floatingLabelText={uiMsg('SP_LOGIN',"Login name")}
                    hintText={uiMsg('SP_LOGIN_HINT',"login")}
                    disabled={user.user_id_locked}
                    value={user.id || ""}
                    onChange={this.handleValue}
                    fullWidth={true}
                    errorText={!form.idChanged || user.login_unique || !user.id ? ""
                      : uiMsg('NU_USERNAME_TAKEN',"Username already taken, pick another") }
              />
          <SelectHomeLocation user={user} ></SelectHomeLocation>
          <TextField data-propname="name"
                    floatingLabelText={uiMsg('SP_FULL_NAME',"Full name")}
                    hintText={uiMsg('SP_FULL_NAME_HINT',"Full name of user.")}
                     fullWidth={true}
                     value={user.name || ""}
                     onChange={this.handleValue}
            />
          <TextField data-propname="email"
                    floatingLabelText={uiMsg('SP_LOGIN_EMAIL',"Email (used to log in)")}
                    hintText={uiMsg('SP_LOGIN_EMAIL_HINT',"Signup email.")}
                   disabled={false}
                   fullWidth={true}
                   value={user.email || ""}
                   onChange={this.handleValue}
                   errorText={
                     !user.validEmail ?
                     uiMsg('SP_INVALID_EMAIL',"Invalid email address") : (
                     form.emailChanged && !user.email_unique ?
                     uiMsg('SP_EMAIL_CLAIMED',"Another user registered with this email")
                     : "") }
            />

           <TextField data-propname="password"
                    floatingLabelText={uiMsg('SP_PASSWORD',"Password (change)")}
                    hintText={uiMsg('SP_PASSWORD_HINT',"New password for user")}
                   fullWidth={true}
                   type={isSettingsPage? 'password' : 'text'}
                   value={user.password || ""}
                   onChange={this.handleValue}
                   autoComplete="new-password"
            />
          { user.password && isSettingsPage ?
             <TextField data-propname="repeat_password"
                      floatingLabelText={uiMsg('SP_REPEAT_PASSWORD',"Repeat password")}
                      hintText={uiMsg('SP_REPEAT_PASSWORD_HINT',"Repeat password")}
                     fullWidth={true}
                     type={'password'}
                     value={user.repeat_password || ""}
                     onChange={this.handleValue}
                     errorText={
                       !user.repeat_password ||
                       user.password == user.repeat_password ?
                       "" : uiMsg('SP_PASSWORD_DONT_MATCH',"Passwords do not match")}
                     /> : null
         }

          <TextField data-propname="phone"
            floatingLabelText={uiMsg('SP_MOBILE_PHONE',"Mobile phone (international format including country code)")}
            hintText={uiMsg('SP_MOBILE_PHONE_HINT',"Mobile phone")}
            fullWidth={true}
            value={user.phone || ""}
            onChange={this.handleValue}
            />

          { user.phone_verified || !user.phone || form.phoneChanged ? null :
            <FlatButton
                secondary={true}
                label={uiMsg('SP_MOBILE_NEEDS_VERIFICATION',"Phone number needs verification")}
                onTouchTap={this.gotoVerifyPhonePage.bind(this)}>
            </FlatButton>
          }
          { user.phone_verified && !form.phoneChanged ?
            (
              AppModel.sendingTestSMS ?
              <RaisedButton
                secondary={true}
                disabled={true}
                label={<span>{uiMsg('SP_SENDING_TEST_SMS',"Sending test SMS")} <RefreshIndicator
                    size={25}
                    left={50}
                    top={5}
                    status="loading"
                    style={refresh}
                    /></span>}
                >
              </RaisedButton>
              :
              <RaisedButton
                primary={true}
                label={uiMsg('SP_B_SEND_TEST_SMS',"Send test SMS")}
                onTouchTap={this.sendTestSMS.bind(this)}>
              </RaisedButton>
            )
            : null
          }
          <SelectLanguageCode target={user} />

            <ListItem
              leftCheckbox={<Checkbox disabled={!user.phone}
                                      checked={!!user.receiveSMSNotifications}
                                      onCheck={this.handleCheckboxToggle(
                                        "receiveSMSNotifications")}/>}
              primaryText={uiMsg('SP_NOTIFICATIONS_LABEL',"Notifications (SMS)")}
              secondaryText={
                !!user.phone ?
                  uiMsg('SP_NOTIFICATION_INFO',"Receive SMS on alarms when jobs you start are complete")
                : uiMsg('SP_NOTIFICATION_REQUIRE_PHONE',"Register mobile phone number above to enable this option")

              }
            />
            <ListItem
              leftCheckbox={<Checkbox checked={!!user.receiveJobCompleteReports}
                                      onCheck={this.handleCheckboxToggle(
                                        "receiveJobCompleteReports")}/>}
              primaryText={uiMsg('SP_JOB_REPORT_LABEL',"Job Reports (email)")}
              secondaryText={uiMsg('SP_JOB_REPORT_INFO',"Get status reports for completed jobs")}
            />
            <ListItem
              leftCheckbox={<Checkbox checked={!!user.receiveInformationEmail}
                                      onCheck={this.handleCheckboxToggle(
                                        "receiveInformationEmail")}/>}
              primaryText={uiMsg('SP_INFO_EMAIL_LABEL',"Information (email)")}
              secondaryText={uiMsg('SP_INFO_EMAIL_INFO',"I want to receive information from Decon-X on e-mail")}
            />
          </div>
        </List>
        <RoleChecker form={form} ></RoleChecker>
        {isSettingsPage || form.idChanged /* cannot replace id and delete at the same time */ ? null :
          <List style={{paddingLeft:'16px'}}>
            <Subheader style={{position:'relative',left:'-16px'}}>
              {uiMsg('SP_DELETE_USER',"Delete user")}
            </Subheader>
            <ListItem
              leftCheckbox={
                <Checkbox
                  data-propname="deleted"
                  disabled={form.idChanged}
                  checked={user.deleted}
                  onCheck={this.toggleDeleted}
                />
              }
              primaryText={uiMsg('SP_DELETE_AND_DEACTIVATE',"Delete (and deactivate login for) this user ?")}
            />
          </List>
        }
        <Toolbar>
          <ToolbarGroup firstChild={true}></ToolbarGroup>
          <ToolbarGroup lastChild={true}>
            <FlatButton
                    onTouchTap={this.cancelUpdate.bind(this)}>
                    {uiMsg('SP_CANCEL',"Cancel")}
            </FlatButton>
            <RaisedButton primary
                          disabled = {!user.isValidEdit
                            || isSettingsPage && user.passwordsDiffer}
                          onTouchTap={this.updateOrDeleteUser.bind(this)}>
                          {user.deleted?
                            uiMsg('SP_DELETE',"Delete")
                            : uiMsg('SP_UPDATE',"Update")
                          }
            </RaisedButton>
          </ToolbarGroup>
        </Toolbar>
        {/* added some space at the bottom to allow some scrolling */}
        <div style={{height:"3em"}}></div>
      </PageLoading>
    );
  }

};



/** WEBPACK FOOTER **
 ** ./src/pages/EditUserPage.jsx
 **/